import { Injectable } from '@angular/core';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { PromptSet, PromptSetApiService, DoraFieldConfig } from './api.service';
import { lastValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ExportService {

  constructor(private promptSetApiService: PromptSetApiService) { }

  /**
   * Export prompt sets to Excel file using import template format
   * All data lives in a single worksheet with columns for prompts, output schema, and summary prompt
   * @param promptSets Array of prompt sets to export
   */
  async exportPromptSetsToExcel(promptSets: PromptSet[]): Promise<void> {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Prompt Sets Export');

    this.setupColumns(worksheet);
    this.styleHeaderRow(worksheet);

    // Pre-fetch summary prompt data for all prompt sets
    const summaryPromptMap = await this.fetchSummaryPrompts(promptSets);

    let rowIndex = 2;

    for (const promptSet of promptSets) {
      const promptSetName = promptSet.name || '';
      const published = promptSet.published ? 'Yes' : 'No';
      const createdAt = promptSet.created_at ? new Date(promptSet.created_at).toLocaleString() : '';
      const numberOfPrompts = promptSet.prompts ? promptSet.prompts.length : 0;

      // Build output schema and summary prompt values for the first row of this prompt set
      const outputSchemaJson = this.buildOutputSchemaJson(promptSet);
      const summaryData = summaryPromptMap.get(promptSet.prompt_set_id);

      if (promptSet.prompts && promptSet.prompts.length > 0) {
        for (let i = 0; i < promptSet.prompts.length; i++) {
          const prompt = promptSet.prompts[i];
          const isFirstRow = i === 0;

          try {
            const promptDetails = await lastValueFrom(
              this.promptSetApiService.getPrompt({ promptIdentifier: (prompt as any).prompt_identifier })
            );

            const promptConfig = promptDetails.promptConfiguration?.[0];
            const context = promptConfig?.prompt?.context || '';

            let promptContent = '';
            if (promptConfig?.prompt?.content) {
              promptContent = promptConfig.prompt.content
                .map((item: any) => item.prompt_content || '')
                .join('\n\n');
            }

            const row = worksheet.addRow({
              promptSetName: isFirstRow ? promptSetName : '',
              promptName: prompt.name || '',
              context: context,
              prompt: promptContent,
              published: isFirstRow ? published : '',
              createdAt: isFirstRow ? createdAt : '',
              numberOfPrompts: isFirstRow ? numberOfPrompts : '',
              outputSchema: isFirstRow ? outputSchemaJson : '',
              summaryPromptName: isFirstRow ? (summaryData?.name || '') : '',
              summaryPromptContext: isFirstRow ? (summaryData?.context || '') : '',
              summaryPromptContent: isFirstRow ? (summaryData?.content || '') : '',
              summaryPromptInputFields: isFirstRow ? (summaryData?.inputFields || '') : ''
            });

            this.applyAlternateRowFill(row, rowIndex);
            rowIndex++;
          } catch (error) {
            console.error(`Error fetching details for prompt ${(prompt as any).prompt_identifier}:`, error);

            const row = worksheet.addRow({
              promptSetName: isFirstRow ? promptSetName : '',
              promptName: prompt.name || '',
              context: 'Error loading details',
              prompt: 'Error loading details',
              published: isFirstRow ? published : '',
              createdAt: isFirstRow ? createdAt : '',
              numberOfPrompts: isFirstRow ? numberOfPrompts : '',
              outputSchema: isFirstRow ? outputSchemaJson : '',
              summaryPromptName: isFirstRow ? (summaryData?.name || '') : '',
              summaryPromptContext: isFirstRow ? (summaryData?.context || '') : '',
              summaryPromptContent: isFirstRow ? (summaryData?.content || '') : '',
              summaryPromptInputFields: isFirstRow ? (summaryData?.inputFields || '') : ''
            });

            this.applyAlternateRowFill(row, rowIndex);
            rowIndex++;
          }
        }
      } else {
        const row = worksheet.addRow({
          promptSetName: promptSetName,
          promptName: '',
          context: '',
          prompt: '',
          published: published,
          createdAt: createdAt,
          numberOfPrompts: numberOfPrompts,
          outputSchema: outputSchemaJson,
          summaryPromptName: summaryData?.name || '',
          summaryPromptContext: summaryData?.context || '',
          summaryPromptContent: summaryData?.content || '',
          summaryPromptInputFields: summaryData?.inputFields || ''
        });

        this.applyAlternateRowFill(row, rowIndex);
        rowIndex++;
      }
    }

    this.capColumnWidths(worksheet);

    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    const filename = `DORA_Prompt_Sets_Export_${timestamp}.xlsx`;

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, filename);
  }

  /**
   * Export a single prompt set to Excel file using import template format
   * @param promptSet Single prompt set to export
   */
  async exportSinglePromptSetToExcel(promptSet: PromptSet): Promise<void> {
    const workbook = new ExcelJS.Workbook();
    const sheetName = this.sanitizeSheetName(promptSet.name || `PromptSet_${promptSet.prompt_set_id}`);
    const worksheet = workbook.addWorksheet(sheetName);

    this.setupColumns(worksheet);
    this.styleHeaderRow(worksheet);

    // Pre-fetch summary prompt data
    const summaryPromptMap = await this.fetchSummaryPrompts([promptSet]);

    const promptSetName = promptSet.name || '';
    const published = promptSet.published ? 'Yes' : 'No';
    const createdAt = promptSet.created_at ? new Date(promptSet.created_at).toLocaleString() : '';
    const numberOfPrompts = promptSet.prompts ? promptSet.prompts.length : 0;

    const outputSchemaJson = this.buildOutputSchemaJson(promptSet);
    const summaryData = summaryPromptMap.get(promptSet.prompt_set_id);

    if (promptSet.prompts && promptSet.prompts.length > 0) {
      for (let i = 0; i < promptSet.prompts.length; i++) {
        const prompt = promptSet.prompts[i];
        const isFirstRow = i === 0;

        try {
          const promptDetails = await lastValueFrom(
            this.promptSetApiService.getPrompt({ promptIdentifier: (prompt as any).prompt_identifier })
          );

          const promptConfig = promptDetails.promptConfiguration?.[0];
          const context = promptConfig?.prompt?.context || '';

          let promptContent = '';
          if (promptConfig?.prompt?.content) {
            promptContent = promptConfig.prompt.content
              .map((item: any) => item.prompt_content || '')
              .join('\n\n');
          }

          const row = worksheet.addRow({
            promptSetName: isFirstRow ? promptSetName : '',
            promptName: prompt.name || '',
            context: context,
            prompt: promptContent,
            published: isFirstRow ? published : '',
            createdAt: isFirstRow ? createdAt : '',
            numberOfPrompts: isFirstRow ? numberOfPrompts : '',
            outputSchema: isFirstRow ? outputSchemaJson : '',
            summaryPromptName: isFirstRow ? (summaryData?.name || '') : '',
            summaryPromptContext: isFirstRow ? (summaryData?.context || '') : '',
            summaryPromptContent: isFirstRow ? (summaryData?.content || '') : '',
            summaryPromptInputFields: isFirstRow ? (summaryData?.inputFields || '') : ''
          });

          this.applyAlternateRowFill(row, i + 2);
        } catch (error) {
          console.error(`Error fetching details for prompt ${(prompt as any).prompt_identifier}:`, error);

          const row = worksheet.addRow({
            promptSetName: isFirstRow ? promptSetName : '',
            promptName: prompt.name || '',
            context: 'Error loading details',
            prompt: 'Error loading details',
            published: isFirstRow ? published : '',
            createdAt: isFirstRow ? createdAt : '',
            numberOfPrompts: isFirstRow ? numberOfPrompts : '',
            outputSchema: isFirstRow ? outputSchemaJson : '',
            summaryPromptName: isFirstRow ? (summaryData?.name || '') : '',
            summaryPromptContext: isFirstRow ? (summaryData?.context || '') : '',
            summaryPromptContent: isFirstRow ? (summaryData?.content || '') : '',
            summaryPromptInputFields: isFirstRow ? (summaryData?.inputFields || '') : ''
          });

          this.applyAlternateRowFill(row, i + 2);
        }
      }
    } else {
      worksheet.addRow({
        promptSetName: promptSetName,
        promptName: '',
        context: '',
        prompt: '',
        published: published,
        createdAt: createdAt,
        numberOfPrompts: numberOfPrompts,
        outputSchema: outputSchemaJson,
        summaryPromptName: summaryData?.name || '',
        summaryPromptContext: summaryData?.context || '',
        summaryPromptContent: summaryData?.content || '',
        summaryPromptInputFields: summaryData?.inputFields || ''
      });
    }

    this.capColumnWidths(worksheet);

    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    const filename = `${sheetName}_Export_${timestamp}.xlsx`;

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, filename);
  }

  /**
   * Setup the standard column definitions for the single-sheet export
   */
  private setupColumns(worksheet: ExcelJS.Worksheet): void {
    worksheet.columns = [
      { header: 'PromptSetName', key: 'promptSetName', width: 30 },
      { header: 'PromptName', key: 'promptName', width: 40 },
      { header: 'Context', key: 'context', width: 50 },
      { header: 'Prompt', key: 'prompt', width: 80 },
      { header: 'Published', key: 'published', width: 15 },
      { header: 'Created At', key: 'createdAt', width: 20 },
      { header: 'Number of Prompts', key: 'numberOfPrompts', width: 20 },
      { header: 'OutputSchema', key: 'outputSchema', width: 50 },
      { header: 'SummaryPromptName', key: 'summaryPromptName', width: 25 },
      { header: 'SummaryPromptContext', key: 'summaryPromptContext', width: 50 },
      { header: 'SummaryPromptContent', key: 'summaryPromptContent', width: 80 },
      { header: 'SummaryPromptInputFields', key: 'summaryPromptInputFields', width: 35 }
    ];
  }

  /**
   * Build a JSON string representation of the output schema for a prompt set
   * Returns empty string if no output schema is defined
   */
  private buildOutputSchemaJson(promptSet: PromptSet): string {
    const outputSchema = (promptSet as any).outputSchema || (promptSet as any).output_schema;
    const fields: DoraFieldConfig[] = outputSchema?.fields || [];

    if (fields.length === 0) {
      return '';
    }

    // Serialize as a compact JSON array of field objects
    const serializable = fields.map(f => {
      const obj: any = { field_name: f.field_name, field_type: f.field_type };
      if (f.default_value != null && f.default_value !== '') {
        obj.default_value = f.default_value;
      }
      if (f.expected_values && f.expected_values.length > 0) {
        obj.expected_values = f.expected_values;
      }
      return obj;
    });

    return JSON.stringify(serializable);
  }

  /**
   * Fetch summary prompt details for an array of prompt sets
   * Returns a Map keyed by prompt_set_id with the summary prompt data
   */
  private async fetchSummaryPrompts(
    promptSets: PromptSet[]
  ): Promise<Map<string, { name: string; context: string; content: string; inputFields: string }>> {
    const result = new Map<string, { name: string; context: string; content: string; inputFields: string }>();

    for (const promptSet of promptSets) {
      const summaryPromptId = `summary_${promptSet.prompt_set_id.replace(/-/g, '')}`;

      try {
        const summaryPromptDetails = await lastValueFrom(
          this.promptSetApiService.getPrompt({ promptIdentifier: summaryPromptId })
        );

        const metadata = summaryPromptDetails?.promptMetadataInformation?.[0];
        const config = summaryPromptDetails?.promptConfiguration?.[0];

        let content = '';
        if (config?.prompt?.content) {
          content = config.prompt.content
            .map((item: any) => item.prompt_content || '')
            .join('\n\n');
        }

        const selectedFields: string[] = metadata?.metadataDict?.summary_input_fields || [];

        result.set(promptSet.prompt_set_id, {
          name: metadata?.name || '',
          context: config?.prompt?.context || '',
          content: content,
          inputFields: selectedFields.join(', ')
        });
      } catch (error) {
        // Summary prompt does not exist for this prompt set — skip
      }
    }

    return result;
  }

  /**
   * Apply standard header row styling to a worksheet
   */
  private styleHeaderRow(worksheet: ExcelJS.Worksheet): void {
    worksheet.getRow(1).font = { bold: true };
    worksheet.getRow(1).fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFE0E0E0' }
    };
  }

  /**
   * Apply alternate row fill for readability
   */
  private applyAlternateRowFill(row: ExcelJS.Row, rowIndex: number): void {
    if ((rowIndex - 2) % 2 === 1) {
      row.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFF8F8F8' }
      };
    }
  }

  /**
   * Cap column widths to a maximum for readability
   */
  private capColumnWidths(worksheet: ExcelJS.Worksheet): void {
    worksheet.columns.forEach(column => {
      if (column.width && column.width > 80) {
        column.width = 80;
      }
    });
  }

  /**
   * Sanitize sheet name to be valid for Excel
   */
  private sanitizeSheetName(name: string): string {
    return name
      .replace(/[\\\/\?\*\[\]]/g, '_')
      .substring(0, 31);
  }
}
